import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import beGetClientSmsText from 'actions/sms/beGetClientSmsText.action';
import { fePolicyFoldToggle } from 'actions/ui.actions';
import ICO_BRANDING from 'assets/images/icons/bookmark-square-black.svg';
import ICO_BUBLE from 'assets/images/online-booking/bubble-purple.svg';
import ICO_DEPOSIT from 'assets/images/online-booking/money-purple.svg';
import Button from 'components/form/Button.component';
import Note from 'components/form/Note.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import withSalonId from 'components/hoc/withSalonId.hoc';
import LINK from 'constants/link.const';
import useAef from 'hooks/useAef.hook';
import useAfn from 'hooks/useAfn.hook';
import useResettingState from 'hooks/useResettingState.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useNcobSettings } from 'providers/NcobSettings.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectPolicyFocusNote, selectPolicyFoldoutOpen } from 'selectors/ui.selectors';
import fromAnyToString from 'utils/converters/fromAnyToString';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import PatchTestPanel from 'settings/online-booking/pages/policy/PatchTestPanel.component';
import Timing from 'settings/online-booking/pages/policy/TimingPanel.component';
import WelcomePanel from 'settings/online-booking/pages/policy/WelcomePanel.component';

import CN from './PolicyPage.module.scss';

const L = logger('PolicyPage');

const LINK_DEPOSIT = 'https://help.getslick.com/en/articles/4893846-all-about-online-booking-deposits';
const LINK_REMINDER = 'https://help.getslick.com/en/articles/8075687-sms-reminder-bolt-on';

const PolicyPage = ({ salonId, beGetClientSmsText, isPolicyFoldoutOpen, shouldFocusNote, fePolicyFoldToggle }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { value, isLoading, error, update } = useNcobSettings();
  const { ncobDepositSettings, salonNcobText } = asObject(value);

  const [text, setText] = useResettingState(salonNcobText, useResettingState.NIL);
  const { val: isDirty, inv: isPristine, yay: markDirty, nay: markPristine } = useTip(false);

  const smsResult = useAef(async () => (await beGetClientSmsText({ salonId })).json, [beGetClientSmsText, salonId]);

  const isEmpty = fromAnyToString(text).length === 0;
  const isDepositMissing = ncobDepositSettings === 'null'; // NOTE: BE sends ncob_deposit_settings as a string "null"
  const isReminderRendered = !asObject(asObject(smsResult).value).enable_sms_after_reminder;
  const linkMarketing = linkTo({ pattern: LINK.reminders, params: { salonId }, fault: '#' });
  const linkSettings = linkTo({ pattern: LINK.salonOnline, params: { salonId }, fault: '#' });

  const [, handleSavePolicyText] = useAfn(
    async ({ raw }) => {
      if (raw === null) return;
      const { ok, value } = await update({ data: { salonNcobText: text } });
      if (!ok) return;
      setText(fromAnyToString(asObject(value).salonNcobText));
      markPristine();
    },
    [markPristine, setText, text, update],
  );

  const handleTextChange = useCallback(
    ({ raw }) => {
      setText(fromAnyToString(raw));
      markDirty();
    },
    [setText, markDirty],
  );

  if (error) L.debug('()', { isLoading, isDirty, text, salonNcobText, error, value });
  L.debug('()', { shouldFocusNote, isPolicyFoldoutOpen });

  return (
    <div
      data-bem="PolicyPage"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      {isLoading && <OverlaySpinner />}

      <WelcomePanel />

      <div data-bem="SetUpView__tips-foldout" className={CN.foldoutContainer}>
        <div className={CN.foldoutHeader} role="button" onClick={fePolicyFoldToggle}>
          <div className={CN.foldoutTitle}>
            <img src={ICO_BRANDING} alt="" className={CN.foldoutIcon} />
            {t('onlineBooking.policyAndTerms.cancellationPolicy.foldoutTitle')}
          </div>

          <Icon
            tag="SetUpView__arrow"
            variant="arrowFoldout"
            selected={!isPolicyFoldoutOpen}
            unselected={isPolicyFoldoutOpen}
            width="l1"
          />
        </div>

        {isPolicyFoldoutOpen && (
          <div className={CN.foldoutContent}>
            <div data-bem="PolicyPage__subtitle" className={CN.container}>
              <div className={CN.sectionTitle}>{t('onlineBooking.policyAndTerms.cancellationPolicy.title1')}</div>
              <div className={CN.text}>{t('onlineBooking.policyAndTerms.cancellationPolicy.description1')}</div>
              <div data-bem="PolicyPage__note-container">
                <Note
                  autofocus={shouldFocusNote}
                  autoscroll
                  hasExternalValue
                  name="note"
                  onChange={handleTextChange}
                  tag="PolicyPage__note"
                  value={text}
                  variant="expanded"
                />
                <div
                  className={cn({
                    [CN.buttonContainer]: true,
                    [CN.hidden]: isPristine || isEmpty,
                  })}
                >
                  <Button variant="primary" onClick={handleSavePolicyText} disabled={isPristine || isEmpty}>
                    {isDirty ? t('common.buttons.saveUpp') : t('common.buttons.editUpp')}
                  </Button>
                </div>
              </div>
              {isDepositMissing && (
                <div className={CN.infoPanel}>
                  <div className={CN.iconContainer}>
                    <img src={ICO_DEPOSIT} alt="" className={CN.icon} />
                    <div className={CN.infoText}>
                      {t('onlineBooking.policyAndTerms.cancellationPolicy.guideline1Part1')}
                      <ExternalLink link={LINK_DEPOSIT} variant="inline">
                        <div className={CN.link}>
                          {t('onlineBooking.policyAndTerms.cancellationPolicy.guideline1Part2')}
                        </div>
                      </ExternalLink>
                      {t('onlineBooking.policyAndTerms.cancellationPolicy.guideline1Part3')}
                    </div>
                  </div>
                  <NavLink to={linkSettings}>
                    <Button variant="secondary">{t('onlineBooking.policyAndTerms.cancellationPolicy.setup')}</Button>
                  </NavLink>
                </div>
              )}
              {isReminderRendered && (
                <div className={CN.infoPanel}>
                  <div className={CN.iconContainer}>
                    <img src={ICO_BUBLE} alt="General Settings" className={CN.icon} />
                    <div className={CN.infoText}>
                      {t('onlineBooking.policyAndTerms.cancellationPolicy.guideline2Part1')}
                      <ExternalLink link={LINK_REMINDER} variant="inline">
                        <div className={CN.link}>
                          {t('onlineBooking.policyAndTerms.cancellationPolicy.guideline2Part2')}
                        </div>
                      </ExternalLink>
                    </div>
                  </div>
                  <NavLink to={linkMarketing}>
                    <Button variant="secondary">{t('onlineBooking.policyAndTerms.cancellationPolicy.setup')}</Button>
                  </NavLink>
                </div>
              )}
            </div>
            <Timing />
            <br />
          </div>
        )}
      </div>

      <PatchTestPanel />
    </div>
  );
};

const mapStateToProps = (state) => ({
  isPolicyFoldoutOpen: selectPolicyFoldoutOpen(state),
  shouldFocusNote: selectPolicyFocusNote(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ beGetClientSmsText, fePolicyFoldToggle }, dispatch);

const PolicyPageHoc = connect(mapStateToProps, mapDispatchToProps)(withSalonId(PolicyPage));

export default PolicyPageHoc;
